import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface TaskEmptyStateProps {
  className?: string
  /** Defaults to a neutral "No tasks here". */
  title?: ReactNode
  /** Why it's empty. */
  description?: ReactNode
  /** The one action that fixes it (design 33b), buttons side by side. */
  actions?: ReactNode
}

/**
 * An empty task list (design 33b): says why it's empty and offers the action
 * that fixes it, right under the message. "All caught up" is only for Inbox
 * and Today at zero — a project that never had a task isn't caught up.
 */
export function TaskEmptyState({ className, title, description, actions }: TaskEmptyStateProps) {
  const { t } = useTranslation('tasks')
  return (
    <div data-testid="task-empty-state" className={cn('mx-auto mt-16 flex max-w-sm flex-col items-center px-6 text-center', className)}>
      <p className="text-[15px] font-semibold text-foreground">{title ?? t('empty.nothingHere')}</p>
      {description && <p className="mt-1 text-[13px] text-muted-foreground">{description}</p>}
      {actions && <div className="mt-4 flex flex-wrap items-center justify-center gap-2">{actions}</div>}
    </div>
  )
}
